import { Fragment, type ReactNode } from 'react'
import type { StoreSupportContact } from '@/api/endpoints'
import { groupSupportContactsByLabel, supportContactHref } from '@/utils/storeSupportContact'
import { isRestrictedSupportContact } from '@/utils/restrictedSocials'

type StoreSupportContactsProps = {
  contacts: StoreSupportContact[]
  /** Класс для подписи (первая строка), напр. text-gray-400 в футере */
  labelClassName?: string
  /** Класс для имени между ссылками */
  nameClassName?: string
  /** Класс для кликабельных значений */
  linkClassName?: string
  className?: string
}

const SupportLine = ({
  group,
  nameClassName,
  linkClassName,
}: {
  group: StoreSupportContact[]
  nameClassName: string
  linkClassName: string
}) => {
  const phones = group.filter((c) => c.contact_type === 'phone')
  const nonPhones = group.filter((c) => c.contact_type !== 'phone')
  const name = group.find((c) => (c.name ?? '').trim())?.name?.trim() ?? ''

  const parts: ReactNode[] = []

  const pushSep = () => {
    if (parts.length > 0) parts.push(' — ')
  }

  phones.forEach((p, i) => {
    pushSep()
    const href = supportContactHref(p)
    parts.push(
      href ? (
        <a key={`phone-${p.value}-${i}`} href={href} className={linkClassName}>
          {p.value}
        </a>
      ) : (
        <span key={`phone-${p.value}-${i}`} className={nameClassName}>
          {p.value}
        </span>
      )
    )
  })

  if (name) {
    pushSep()
    parts.push(
      <span key={`name-${name}`} className={nameClassName}>
        {name}
      </span>
    )
  }

  nonPhones.forEach((c, i) => {
    pushSep()
    const href = supportContactHref(c)
    const isHttp = Boolean(href && href.startsWith('http'))
    parts.push(
      href ? (
        <a
          key={`other-${c.contact_type}-${c.value}-${i}`}
          href={href}
          {...(isHttp ? { target: '_blank', rel: 'noreferrer' } : {})}
          className={linkClassName}
        >
          {c.value}
        </a>
      ) : (
        <span key={`other-${c.contact_type}-${c.value}-${i}`} className={nameClassName}>
          {c.value}
        </span>
      )
    )
  })

  return <div className="text-sm text-wrap">{parts}</div>
}

const StoreSupportContacts = ({
  contacts,
  labelClassName = 'text-gray-600',
  nameClassName = 'text-gray-700',
  linkClassName = 'text-primary hover:underline',
  className = '',
}: StoreSupportContactsProps) => {
  const visibleContacts = contacts.filter((contact) => !isRestrictedSupportContact(contact))

  if (!visibleContacts.length) return null

  const groups = groupSupportContactsByLabel(visibleContacts)

  return (
    <div className={`flex flex-col space-y-2 ${className}`}>
      {groups.map((group, gi) => {
        const label = (group[0]?.label ?? '').trim()
        return (
          <Fragment key={gi}>
            {label ? (
              <p className={`text-sm ${labelClassName}`}>{label}:</p>
            ) : null}
            <SupportLine
              group={group}
              nameClassName={nameClassName}
              linkClassName={linkClassName}
            />
          </Fragment>
        )
      })}
    </div>
  )
}

export default StoreSupportContacts
